import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Edit, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { format } from "date-fns";
import type { AdminUser } from "../types";

interface UserManagementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  users: AdminUser[];
  totalUsers: number;
  filter?: (user: AdminUser) => boolean;
  filterLabel?: string;
  isLoading: boolean;
  onRoleChange: (userId: string, email: string, newRole: string) => void;
  onAccessToggle: (userId: string, email: string, currentStatus: boolean) => void;
  onEditUser: (user: AdminUser) => void;
  isUpdating: boolean;
}

export function UserManagementDialog({
  open,
  onOpenChange,
  users,
  totalUsers,
  filter,
  filterLabel,
  isLoading,
  onRoleChange,
  onAccessToggle,
  onEditUser,
  isUpdating
}: UserManagementDialogProps) {
  const [sortField, setSortField] = useState<string>("createdAt");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const toggleSort = (field: string) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("asc"); }
  };
  const SortIcon = ({ field }: { field: string }) => sortField !== field
    ? <ArrowUpDown className="h-3 w-3 ml-1 inline opacity-40" />
    : sortDir === "asc" ? <ArrowUp className="h-3 w-3 ml-1 inline" /> : <ArrowDown className="h-3 w-3 ml-1 inline" />;
  const sortedUsers = (filter ? users.filter(filter) : users).slice().sort((a, b) => {
    const dir = sortDir === "asc" ? 1 : -1;
    if (sortField === "email") return dir * a.email.localeCompare(b.email);
    if (sortField === "name") return dir * (a.name || "").localeCompare(b.name || "");
    if (sortField === "role") return dir * a.role.localeCompare(b.role);
    if (sortField === "lastLogin") return dir * (new Date(a.lastLogin || 0).getTime() - new Date(b.lastLogin || 0).getTime());
    if (sortField === "createdAt") return dir * (new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
    return 0;
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] md:max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{filterLabel || "All Users"} ({filter ? users.filter(filter).length : totalUsers})</DialogTitle>
          <DialogDescription>List of registered users with ChefAiQ access</DialogDescription>
        </DialogHeader>
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : users && users.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("email")}>Email<SortIcon field="email" /></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("name")}>Name<SortIcon field="name" /></TableHead>
                  <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("role")}>Role<SortIcon field="role" /></TableHead>
                  <TableHead>Access</TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("lastLogin")}>Last Login<SortIcon field="lastLogin" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("createdAt")}>Registered<SortIcon field="createdAt" /></TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{user.name || '-'}</TableCell>
                  <TableCell>
                    <Select
                      value={user.role}
                      onValueChange={(value) => onRoleChange(user.id, user.email, value)}
                      disabled={isUpdating}
                    >
                      <SelectTrigger className="w-32">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="user">User</SelectItem>
                        <SelectItem value="admin">Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={user.isActive}
                      onCheckedChange={() => onAccessToggle(user.id, user.email, user.isActive)}
                      disabled={isUpdating}
                    />
                  </TableCell>
                  <TableCell>{user.lastLogin ? format(new Date(user.lastLogin), 'MMM d, yyyy') : 'Never'}</TableCell>
                  <TableCell>{format(new Date(user.createdAt), 'MMM d, yyyy')}</TableCell>
                  <TableCell>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEditUser(user)}
                      disabled={isUpdating}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>
        ) : (
          <p className="text-center text-muted-foreground py-8">No users found</p>
        )}
      </DialogContent>
    </Dialog>
  );
}
